import Logger from "@/utility/logger";
import { TODO_TEXT_COLOR } from "@/utility/captionTodoColor";
import { resolveTodoSpanRanges } from "./wordTodoSpan";
import { applyTaskItems } from "./wordTaskItems";
import { hasJenlyComment, loadExistingComments } from "./wordExistingComments";

const MISSING_DATA_TOKEN = "JENLY_NO_DATA";

const MISSING_DATA_REPLACEMENT = "<missing data>";

export const parseMissingDataMessage = (tokenText: string): string => {
  let normalizedText = tokenText.trim();
  
  // Replace the first occurrence of '(', '[', or '{' after JENLY_NO_DATA with '{'
  normalizedText = normalizedText.replace(/(JENLY_NO_DATA\s*)[(\[{]/, '$1{');
  
  // Replace the last occurrence of ')', ']', or '}' with '}'
  if (normalizedText.endsWith(')') || normalizedText.endsWith(']')) {
    normalizedText = normalizedText.slice(0, -1) + '}';
  }

/**
 * Matches a token of the form: JENLY_NO_DATA { ... }
 * 
 * \s*       — optional whitespace between the token name and opening brace
 * {         — literal opening brace
 * ([\s\S]*) — capture group: everything inside the braces, including newlines
 *             [\s\S] is used instead of . to match across multiple lines
 * }         — literal closing brace
 * 
 * tokenMatch[1] contains the content inside the curly braces.
*/
  const tokenMatch = normalizedText.match(/JENLY_NO_DATA\s*\{([\s\S]*)\}/);
  const rawObjectContent = tokenMatch ? tokenMatch[1] : "";
  const objectText = `{${rawObjectContent}}`;
/**
 * Converts unquoted JS-style object keys to quoted JSON keys,
 * making the string parseable by JSON.parse().
 *
 * Example:  { message: "Hello, how are you?" } to { "message": "Hello, how are you?"}
 *
 * Regex breakdown:  /([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g
 *
 * ([{,]\s*)         — Group 1: a { or , (key boundary) followed by optional whitespace.
 *                     Anchors the match to the start of an object key.
 *
 * ([A-Za-z_$][\w$]*)— Group 2: a valid JS identifier (unquoted key).
 *                     Must start with a letter, _ or $, then any word char / $.
 *
 * \s*               — optional whitespace before the colon
 * :                 — the key-value separator colon
 *
 * Replacement '$1"$2":'
 *   $1 → preserves the { or , + whitespace
 *   $2 → wraps the bare identifier in double quotes
 *   :  → puts the colon back
*/
  const jsonLikeText = objectText.replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":');

  try {
    const parsed = JSON.parse(jsonLikeText);
    if (typeof parsed.message === "string" && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
/**
 * Fallback regex to extract the "message" value when JSON.parse() fails.
 * Handles loosely-formatted or malformed object strings directly.
 *
 * Matches patterns like:
 *   message: "hello"
 *   "message": 'hello'
 *   Message: "Upload the file"    ← /i makes it case-insensitive
 *
 * Regex breakdown:  /["']?message["']?\s*:\s*["']([^"']+)["']/i
 *
 * ["']?       — optional opening quote around the key (handles "message" or message)
 * message     — literal key name
 * ["']?       — optional closing quote around the key
 * \s*:\s*     — colon with optional whitespace on either side
 * ["']        — opening quote of the value (single or double)
 * ([^"']+)    — capture group: the value — one or more chars that are NOT a quote
 *               stops at the closing quote, so it won't overrun the string
 * ["']        — closing quote of the value
 * /i          — case-insensitive, so "Message" or "MESSAGE" also match
 *
 * messageMatch[1] contains the extracted message value.
 */
    const messageMatch = rawObjectContent.match(/["']?message["']?\s*:\s*["']([^"']+)["']/i);
    if (messageMatch && messageMatch[1] && messageMatch[1].trim()) {
      return messageMatch[1].trim();
    }

    // Fallback for completely unquoted or unmatched quote message values (e.g. ```message: "test``` or ```message: test``` or ```message:test'```)
    const unquotedMatch = rawObjectContent.match(/["']?message["']?\s*:\s*([\s\S]+)/i);
    if (unquotedMatch && unquotedMatch[1]) {
      let cleanVal = unquotedMatch[1].trim();

      // Strip leading quote if present
      if (cleanVal.startsWith('"') || cleanVal.startsWith("'")) {
        cleanVal = cleanVal.slice(1);
      }

      // Strip trailing quote if present
      if (cleanVal.endsWith('"') || cleanVal.endsWith("'")) {
        cleanVal = cleanVal.slice(0, -1);
      }

      cleanVal = cleanVal.trim();
      if (cleanVal) {
        return cleanVal;
      }
    }
  }

  return "missing";
};

export const wordTask = async (
  context: Word.RequestContext,
  word: string,
  rangeToSearch?: Word.Range,
  shouldHighlight?: boolean,
  shouldGenerateComments?: boolean
) => {
  try {
    Logger.debug("wordTask.ts: Scanning template for ..." + word);

    const searchRange = rangeToSearch ? rangeToSearch : context.document.body;

    const isTodo = word.toUpperCase().includes("TODO");
    if (isTodo) await applyTaskItems(context, searchRange, shouldHighlight, shouldGenerateComments);
    const isRawMissingDataToken = word === MISSING_DATA_TOKEN;
    const isInlineMissingDataToken = word === "[Missing data:";
    const isMissingDataToken = isRawMissingDataToken || isInlineMissingDataToken;
    const searchOptions =
      isTodo
        ? { matchCase: false, matchWholeWord: false }
        : isMissingDataToken
        ? { matchCase: true, matchWholeWord: false }
        : { matchCase: false, ignoreSpace: true, ignorePunct: true };

    const searchTargets = isTodo ? ["TODO:", "TODO."] : [word];
    const wordSearchResultsList: Word.RangeCollection[] = [];
    for (const target of searchTargets) {
      const results = searchRange.search(target, searchOptions);
      results.load("items");
      wordSearchResultsList.push(results);
    }
    await context.sync();

    const allWordSearchResults: Word.Range[] = [];
    for (const res of wordSearchResultsList) {
      if (res.items && res.items.length > 0) {
        allWordSearchResults.push(...res.items);
      }
    }

    if (allWordSearchResults.length > 0) {
      Logger.debug(`wordTask.ts: Found ${allWordSearchResults.length} occurrences of "${word}", isTodo: ${isTodo}`);
      const commentActions: { range: Word.Range; message: string; isTodo?: boolean }[] = [];

      const todoMatches: Word.Range[] = [];

      for (const wordSearchRange of allWordSearchResults) {
        if (isMissingDataToken) {
          const parentParagraph = wordSearchRange.paragraphs.getFirst();
          const currentParaEnd = parentParagraph.getRange(Word.RangeLocation.end);
          const currentScope = wordSearchRange.expandTo(currentParaEnd);

          currentScope.load("text");
          await context.sync();

          const scopeText = currentScope.text;
          const bracketMatch = isInlineMissingDataToken ? scopeText.match(/\]/) : scopeText.match(/[}\])]/);

          if (!bracketMatch) {
            Logger.warn("wordTask.ts: Missing data token did not include a closing brace/bracket/parenthesis.");
            continue;
          }

          const endChar = bracketMatch[0];
          const endTokenSearch = currentScope.search(endChar, { matchWildcards: false });
          endTokenSearch.load("items");
          await context.sync();

          if (endTokenSearch.items.length === 0) {
            Logger.warn(`wordTask.ts: Missing data token closing character "${endChar}" could not be located.`);
            continue;
          }

          const tokenRange = wordSearchRange.expandTo(endTokenSearch.items[0]);
          tokenRange.load("text");
          await context.sync();

          if (isInlineMissingDataToken) {
            const rawMessage = tokenRange.text.replace(/^\[Missing data:\s*|\]$/g, "").trim();
            const message = rawMessage || "required source data";
            if (shouldHighlight) tokenRange.font.highlightColor = null;
            if (shouldGenerateComments) {
              const [existing] = await loadExistingComments(context, [tokenRange]);
              if (!hasJenlyComment(existing)) {
                tokenRange.insertComment(`Jenly.ai:  Please upload the ${message} data and try again.`);
              }
            }
          } else {
            const message = parseMissingDataMessage(tokenRange.text);
            const replacementText = `[Missing data: ${message}]`;
            const replacementRange = tokenRange.insertText(replacementText, Word.InsertLocation.replace);
            if (shouldHighlight) replacementRange.font.highlightColor = null;
            if (shouldGenerateComments) {
              const [existing] = await loadExistingComments(context, [replacementRange]);
              if (!hasJenlyComment(existing)) {
                replacementRange.insertComment(`Jenly.ai:  Please upload the ${message} data and try again.`);
              }
            }
          }

          continue;
        }

        if (!isTodo) {
          commentActions.push({
            range: wordSearchRange,
            message: `Jenly.AI: ${word}`,
            isTodo: false,
          });
          continue;
        }

        todoMatches.push(wordSearchRange);
      }

      await context.sync();

      Logger.debug(`wordTask.ts: Processing ${todoMatches.length} TODO matches`);

      const todoRanges = await resolveTodoSpanRanges(context, todoMatches);

      for (const finalRange of todoRanges) {
        finalRange.load("text");
      }

      await context.sync();

      for (const finalRange of todoRanges) {
        const rawText = finalRange.text || "";
        const replaceWordRegex = isTodo ? /^\s*TODO[:.]\s*/i : new RegExp(`${word}\\s*`, "i");

        let cleanContent = rawText
          .replace(replaceWordRegex, "")
          .replace(/\.$/, "")
          .replace(/[\r\n\v\u000b\u2028\u2029]+/g, " ")
          .trim();

        if (cleanContent.length > 512) cleanContent = cleanContent.substring(0, 512);

        commentActions.push({
          range: finalRange,
          message: cleanContent.length > 0 ? `Jenly.AI: ${cleanContent}` : "Jenly.AI: TODO",
          isTodo,
        });
      }

      const existingComments = shouldGenerateComments
        ? await loadExistingComments(
            context,
            commentActions.map((action) => action.range)
          )
        : [];

      commentActions.forEach((action, actionIndex) => {
        if (action.isTodo) {
          if (shouldHighlight) {
            action.range.font.color = TODO_TEXT_COLOR;
            action.range.font.bold = true;
            action.range.font.highlightColor = null;
          }
        } else {
          if (shouldHighlight) action.range.font.highlightColor = null;
        }
        if (shouldGenerateComments && action.message && !hasJenlyComment(existingComments[actionIndex])) {
          action.range.insertComment(action.message);
        }
      });

      await context.sync();
    }
  } catch (error) {
    Logger.error(`wordTask.ts: Error processing word task for ${word}`, error);
  }
};
